import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as Tone from 'tone';
import { parseGIF, decompressFrames } from 'gifuct-js';
import { LofigenEngine } from '../utils/lofigenEngine';
import biosFont from '../desktop/assets/fonts/Ac437_IBM_BIOS.ttf';

// Using local pixel GIFs from /public (same as /crt) so Things show
// immediately with no CORS dependency. Can be swapped back to S3
// (getPixelatedImageUrl) once bucket CORS is enabled.
const LOCAL_PIXELS = ['/pixel-1.gif', '/pixel-2.gif', '/pixel-3.gif'];

// Note name lookup.
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// CRT shader — vertex (copied from CRTLab)
const VERT_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_position.x * 0.5 + 0.5, 1.0 - (a_position.y * 0.5 + 0.5));
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// CRT shader — fragment (copied from CRTLab)
const FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_time;
uniform vec2 u_resolution;

vec2 barrelDistort(vec2 uv, float amt) {
  vec2 cc = uv - 0.5;
  float dist = dot(cc, cc);
  return uv + cc * dist * amt;
}

float scanline(float y, float res) {
  return 0.92 + 0.08 * sin(y * res * 3.14159 * 2.0);
}

vec3 shadowMask(vec2 fragCoord) {
  float x = mod(fragCoord.x, 3.0);
  vec3 mask = vec3(0.8);
  if (x < 1.0) mask.r = 1.0;
  else if (x < 2.0) mask.g = 1.0;
  else mask.b = 1.0;
  return mask;
}

void main() {
  vec2 uv = barrelDistort(v_uv, 0.15);
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  float aberr = 0.002;
  float r = texture2D(u_texture, vec2(uv.x + aberr, uv.y)).r;
  float g = texture2D(u_texture, uv).g;
  float b = texture2D(u_texture, vec2(uv.x - aberr, uv.y)).b;
  vec3 color = vec3(r, g, b);
  float scan = scanline(uv.y, u_resolution.y * 0.5);
  color *= scan;
  vec2 fragCoord = uv * u_resolution;
  color *= shadowMask(fragCoord);
  vec3 bloom = vec3(0.0);
  for (float i = -2.0; i <= 2.0; i += 1.0) {
    for (float j = -2.0; j <= 2.0; j += 1.0) {
      vec2 offset = vec2(i, j) / u_resolution;
      bloom += texture2D(u_texture, uv + offset).rgb;
    }
  }
  bloom /= 25.0;
  color += bloom * 0.08;
  vec2 vig = uv * (1.0 - uv);
  float vigAmount = vig.x * vig.y * 15.0;
  vigAmount = pow(vigAmount, 0.25);
  color *= vigAmount;
  float barPos = fract(u_time * 0.08);
  float barDist = abs(uv.y - barPos);
  float bar = smoothstep(0.06, 0.0, barDist) * 0.12;
  color += color * bar;
  color *= 0.98 + 0.02 * sin(u_time * 8.0);
  color *= vec3(1.0, 1.02, 0.98);
  color *= 1.3;
  gl_FragColor = vec4(color, 1.0);
}
`;

function createShaderProgram(gl) {
  const vs = gl.createShader(gl.VERTEX_SHADER);
  gl.shaderSource(vs, VERT_SRC);
  gl.compileShader(vs);
  if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) return null;
  const fs = gl.createShader(gl.FRAGMENT_SHADER);
  gl.shaderSource(fs, FRAG_SRC);
  gl.compileShader(fs);
  if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  return prog;
}

// Inject IBM BIOS font + CRT glow styles (same as CRTLab)
const fontStyleId = 'crt-bios-font';
if (typeof document !== 'undefined' && !document.getElementById(fontStyleId)) {
  const style = document.createElement('style');
  style.id = fontStyleId;
  style.textContent = `
    @font-face {
      font-family: 'IBM BIOS';
      src: url(${biosFont}) format('truetype');
      font-weight: normal;
      font-style: normal;
    }
    .crt-glow {
      text-shadow: 0 0 2px rgba(255,255,255,0.6), 0 0 6px rgba(255,255,255,0.2);
      image-rendering: pixelated;
    }
    .crt-btn {
      background: none; border: none;
      color: rgba(255,255,255,0.6);
      cursor: pointer; padding: 4px;
      font-family: 'IBM BIOS', 'Lucida Console', monospace;
      text-shadow: 0 0 2px rgba(255,255,255,0.3);
      font-size: 16px; line-height: 1;
      image-rendering: pixelated;
    }
    .crt-btn:hover {
      color: #ffffff;
      text-shadow: 0 0 2px rgba(255,255,255,0.8), 0 0 10px rgba(255,255,255,0.3);
    }
    .crt-btn.active { color: #ffffff; text-shadow: 0 0 2px rgba(255,255,255,0.8); }
    @keyframes crt-flicker {
      0%, 100% { opacity: 0.92; }
      3% { opacity: 0.87; }
      6% { opacity: 0.95; }
      50% { opacity: 0.93; }
      53% { opacity: 0.88; }
      56% { opacity: 0.94; }
    }
    .crt-ui-overlay { animation: crt-flicker 4s infinite; }
  `;
  document.head.appendChild(style);
}

function trackSig(track) {
  if (!track) return '';
  const progIds = Array.isArray(track.progression)
    ? track.progression.map((c) => Array.isArray(c) ? c[0] : '?').join('-')
    : '';
  return `${track.key}|${track.mode}|${track.bpm}|${track.progTemplate}|${progIds}`;
}

function formatGenerationText(track) {
  if (!track) return '';
  const key = (typeof track.key === 'number') ? NOTE_NAMES[((track.key % 12) + 12) % 12] : '';
  const mode = (track.mode || 'major').toUpperCase();
  const bpm = Math.round(track.bpm || 0);
  const chordal = track.instruments?.chordal || '';
  const lead = track.instruments?.lead || '';
  const bass = track.instruments?.bass || '';
  let prog = '';
  if (Array.isArray(track.progression)) {
    prog = track.progression
      .map((c) => Array.isArray(c) ? NOTE_NAMES[((c[0] % 12) + 12) % 12] : '?')
      .join(' ');
  }
  return `${key} ${mode}. ${bpm} BPM. ${chordal}, ${lead}, ${bass}. ${prog}.`;
}

export default function Station() {
  const imgCanvasRef = useRef(null);
  const glCanvasRef = useRef(null);

  const engineRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState('idle');
  const [track, setTrack] = useState(null);
  const trackSigRef = useRef('');
  const trackPollRef = useRef(null);

  const thingFramesRef = useRef(null);
  const [thingId, setThingId] = useState(null);

  // Static ref — drives the TV-static visual on imgCanvas when true. Shown
  // before the radio starts playing, and briefly on each track change.
  const showStaticRef = useRef(true);
  const staticClearTimeoutRef = useRef(null);

  const [infoOpen, setInfoOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);

  // Text shown over the image — generation info per track.
  const signalTextRef = useRef(null);
  const signalStartTimeRef = useRef(0);

  // --- Engine init ---
  useEffect(() => {
    const engine = new LofigenEngine();
    engineRef.current = engine;
    window.lofiEngine = engine;
    window.Tone = Tone;
    // Seed a Thing ID immediately so a GIF loads on mount.
    setThingId(1 + Math.floor(Math.random() * 10000));
    return () => {
      if (trackPollRef.current) clearInterval(trackPollRef.current);
      clearTimeout(staticClearTimeoutRef.current);
      engine.dispose();
    };
  }, []);

  // --- Auto-hide controls ---
  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  useEffect(() => {
    const onMove = () => showControls();
    window.addEventListener('mousemove', onMove);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 4000);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(hideTimerRef.current);
    };
  }, [showControls]);

  // --- GIF decoding (same as CRTLab) ---
  const decodeGif = useCallback(async (url) => {
    const resp = await fetch(url);
    const buff = await resp.arrayBuffer();
    const gif = parseGIF(buff);
    const frames = decompressFrames(gif, true);
    if (frames.length === 0) return null;
    const gifW = frames[0].dims.width;
    const gifH = frames[0].dims.height;
    const compCanvas = document.createElement('canvas');
    compCanvas.width = gifW;
    compCanvas.height = gifH;
    const compCtx = compCanvas.getContext('2d');
    const composited = [];
    for (const frame of frames) {
      const patch = new ImageData(new Uint8ClampedArray(frame.patch), frame.dims.width, frame.dims.height);
      if (frame.disposalType === 2) compCtx.clearRect(0, 0, gifW, gifH);
      const patchCanvas = document.createElement('canvas');
      patchCanvas.width = frame.dims.width;
      patchCanvas.height = frame.dims.height;
      patchCanvas.getContext('2d').putImageData(patch, 0, 0);
      compCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);
      const snapshot = compCtx.getImageData(0, 0, gifW, gifH);
      composited.push({ imageData: snapshot, delay: frame.delay || 100, width: gifW, height: gifH });
    }
    return composited;
  }, []);

  // Load Thing GIF when thingId changes — currently using local pixel GIFs
  // from /public. Thing ID picks which one.
  useEffect(() => {
    if (thingId == null) return;
    let cancelled = false;
    const url = LOCAL_PIXELS[thingId % LOCAL_PIXELS.length];
    decodeGif(url)
      .then((frames) => {
        if (cancelled) return;
        thingFramesRef.current = frames;
        setLoaded(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [thingId, decodeGif]);

  // --- Track polling ---
  const startTrackPolling = useCallback(() => {
    if (trackPollRef.current) clearInterval(trackPollRef.current);
    const tick = () => {
      const engine = engineRef.current;
      if (!engine) return;
      const t = engine.getCurrentTrack();
      if (!t) return;
      const sig = trackSig(t);
      if (sig !== trackSigRef.current) {
        trackSigRef.current = sig;
        const id = 1 + Math.floor(Math.random() * 10000);
        setThingId(id);
        setTrack(t);
        signalTextRef.current = formatGenerationText(t);
        signalStartTimeRef.current = performance.now();
        // Flash static on track transition.
        showStaticRef.current = true;
        clearTimeout(staticClearTimeoutRef.current);
        staticClearTimeoutRef.current = setTimeout(() => {
          showStaticRef.current = false;
        }, 500);
      } else {
        setTrack(t);
      }
    };
    tick();
    trackPollRef.current = setInterval(tick, 1000);
  }, []);

  // --- Controls ---
  const handlePlay = useCallback(async () => {
    const engine = engineRef.current;
    if (!engine || status === 'playing') return;
    if (!engine._inited) { setStatus('loading'); await engine.init(); }
    await engine.start();
    setStatus('playing');
    startTrackPolling();
    showControls();
  }, [status, startTrackPolling, showControls]);

  const handleStop = useCallback(() => {
    engineRef.current?.stop();
    if (trackPollRef.current) clearInterval(trackPollRef.current);
    setStatus('paused');
    showControls();
  }, [showControls]);

  const handleNext = useCallback(async () => {
    const engine = engineRef.current;
    if (!engine) return;
    // Flash static immediately — the polling loop will clear it 500ms after
    // the new track is detected.
    showStaticRef.current = true;
    clearTimeout(staticClearTimeoutRef.current);
    if (!engine._inited) { setStatus('loading'); await engine.init(); await engine.start(); }
    else { engine.skip(); }
    setStatus('playing');
    startTrackPolling();
    showControls();
  }, [startTrackPolling, showControls]);

  // --- Keyboard ---
  useEffect(() => {
    function onKey(e) {
      if (e.key === ' ') {
        e.preventDefault();
        if (status === 'playing') handleStop(); else handlePlay();
      } else if (e.key === 'ArrowRight' || e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'i' || e.key === 'I') {
        setInfoOpen(o => !o);
        showControls();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [status, handlePlay, handleStop, handleNext, showControls]);

  // --- Canvas resize ---
  useEffect(() => {
    function onResize() {
      const w = window.innerWidth, h = window.innerHeight;
      if (imgCanvasRef.current) { imgCanvasRef.current.width = w; imgCanvasRef.current.height = h; }
      if (glCanvasRef.current) { glCanvasRef.current.width = w; glCanvasRef.current.height = h; }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // --- Drawing loop: Thing GIF + signal text onto imgCanvas (same pattern as CRTLab) ---
  useEffect(() => {
    const canvas = imgCanvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    let frameIndex = 0;
    let lastFrameTime = performance.now();
    let rafId;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    // Pre-allocated static noise buffer (small, scaled up for chunky pixels).
    const staticW = 320;
    const staticH = 240;
    const staticCanvas = document.createElement('canvas');
    staticCanvas.width = staticW;
    staticCanvas.height = staticH;
    const staticCtx = staticCanvas.getContext('2d');
    const staticImgData = staticCtx.createImageData(staticW, staticH);
    const staticPixels = staticImgData.data;

    function drawFrame(now) {
      const W = canvas.width, H = canvas.height;
      const frames = thingFramesRef.current;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      // TV static — shown before first play + briefly on each track change
      if (showStaticRef.current) {
        for (let i = 0; i < staticPixels.length; i += 4) {
          const v = Math.random() * 255 | 0;
          staticPixels[i] = v;
          staticPixels[i + 1] = v;
          staticPixels[i + 2] = v;
          staticPixels[i + 3] = 255;
        }
        staticCtx.putImageData(staticImgData, 0, 0);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(staticCanvas, 0, 0, W, H);
        rafId = requestAnimationFrame(drawFrame);
        return;
      }

      if (frames && frames.length) {
        const frame = frames[frameIndex];
        if (frame) {
          const elapsed = now - lastFrameTime;
          if (elapsed >= (frame.delay <= 0 ? 100 : frame.delay)) {
            frameIndex = (frameIndex + 1) % frames.length;
            lastFrameTime = now;
          }
          const current = frames[frameIndex];
          if (tempCanvas.width !== current.width || tempCanvas.height !== current.height) {
            tempCanvas.width = current.width;
            tempCanvas.height = current.height;
          }
          tempCtx.putImageData(current.imageData, 0, 0);
          ctx.imageSmoothingEnabled = false;
          const scale = Math.max(W / current.width, H / current.height);
          const sw = current.width * scale, sh = current.height * scale;
          ctx.drawImage(tempCanvas, (W - sw) / 2, (H - sh) / 2, sw, sh);
        }
      }

      // Signal text (same rendering style as CRTLab — typed out + held)
      const sigText = signalTextRef.current;
      if (sigText) {
        const sigElapsed = (now - signalStartTimeRef.current) / 1000;
        const sentences = sigText.match(/[^.!?]+[.!?]?\s*/g) || [sigText];
        const CHARS_PER_SEC = 12;
        const HOLD_SEC = 2;
        let timeAcc = 0;
        let revealed = '';
        for (let s = 0; s < sentences.length; s++) {
          const typeTime = sentences[s].length / CHARS_PER_SEC;
          if (sigElapsed < timeAcc + typeTime) {
            const chars = Math.floor((sigElapsed - timeAcc) * CHARS_PER_SEC);
            revealed += sentences[s].slice(0, chars);
            break;
          }
          revealed += sentences[s];
          timeAcc += typeTime;
          if (sigElapsed < timeAcc + HOLD_SEC) break;
          timeAcc += HOLD_SEC;
        }

        if (revealed.length > 0) {
          ctx.save();
          const fontSize = Math.max(14, Math.floor(W * 0.018));
          ctx.font = fontSize + 'px "IBM BIOS", "Lucida Console", monospace';
          ctx.textBaseline = 'top';
          const maxWidth = W * 0.7;
          const words = revealed.split(' ');
          const lines = [];
          let line = '';
          for (const word of words) {
            const test = line ? line + ' ' + word : word;
            if (ctx.measureText(test).width > maxWidth && line) {
              lines.push(line);
              line = word;
            } else {
              line = test;
            }
          }
          if (line) lines.push(line);
          const lineHeight = fontSize * 1.6;
          const totalH = lines.length * lineHeight;
          const startY = (H - totalH) / 2;
          const startX = W * 0.15;
          ctx.fillStyle = '#ffffff';
          for (let i = 0; i < lines.length; i++) {
            ctx.fillText(lines[i], startX, startY + i * lineHeight);
          }
          ctx.restore();
        }
      }

      rafId = requestAnimationFrame(drawFrame);
    }
    rafId = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // --- WebGL CRT (exact copy from CRTLab) ---
  useEffect(() => {
    const glCanvas = glCanvasRef.current;
    const sourceCanvas = imgCanvasRef.current;
    if (!glCanvas || !sourceCanvas) return;
    glCanvas.width = window.innerWidth;
    glCanvas.height = window.innerHeight;
    const gl = glCanvas.getContext('webgl', { alpha: false, antialias: false });
    if (!gl) return;
    const prog = createShaderProgram(gl);
    if (!prog) return;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_position');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uRes = gl.getUniformLocation(prog, 'u_resolution');
    const startTime = performance.now();
    let rafId;
    function render() {
      const t = (performance.now() - startTime) / 1000;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
      gl.viewport(0, 0, glCanvas.width, glCanvas.height);
      gl.uniform1f(uTime, t);
      gl.uniform2f(uRes, glCanvas.width, glCanvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      rafId = requestAnimationFrame(render);
    }
    render();
    return () => cancelAnimationFrame(rafId);
  }, [loaded]);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#000', overflow: 'hidden', cursor: controlsVisible ? 'default' : 'none' }}>
      <canvas ref={imgCanvasRef} style={{ display: 'none' }} />
      <canvas ref={glCanvasRef} style={{ display: 'block', width: '100vw', height: '100vh' }} />

      <div className="crt-ui-overlay" style={{
        position: 'absolute', top: 16, right: 16, zIndex: 20,
        opacity: controlsVisible ? 1 : 0,
        pointerEvents: controlsVisible ? 'auto' : 'none',
        fontFamily: "'IBM BIOS', monospace",
        fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textAlign: 'right',
      }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <button className={`crt-btn ${status === 'playing' ? 'active' : ''}`} onClick={status === 'playing' ? handleStop : handlePlay} style={{ fontSize: 12 }}>
            {status === 'loading' ? '...' : status === 'playing' ? '[||]' : '[|>]'}
          </button>
          <button className="crt-btn" onClick={handleNext} style={{ fontSize: 12 }}>[&gt;&gt;]</button>
          <button className="crt-btn" onClick={() => setInfoOpen(o => !o)} style={{ fontSize: 12 }}>
            {infoOpen ? '[X]' : '[?]'}
          </button>
        </div>

        {infoOpen && (
          <div className="crt-glow" style={{
            marginTop: 6, padding: '10px 12px',
            background: 'rgba(0,0,0,0.95)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 9, lineHeight: '2',
            fontFamily: "'IBM BIOS', monospace", letterSpacing: 1,
            textAlign: 'left',
            minWidth: 240,
            maxWidth: 320,
          }}>
            {/* === NOW (primary) === */}
            <div style={{
              color: 'rgba(255,255,255,1)',
              fontSize: 11, letterSpacing: 3, marginBottom: 6,
            }}>
              NOW PLAYING
            </div>

            {track ? (
              <div style={{ color: 'rgba(255,255,255,0.85)' }}>
                {thingId != null && <div>thing ....... {String(thingId).padStart(4, '0')}</div>}
                <div>
                  mode ........ {track.key != null ? `${NOTE_NAMES[((track.key % 12) + 12) % 12]} ` : ''}
                  {(track.mode || 'major')}
                </div>
                <div>bpm ......... {Math.round(track.bpm || 0)}</div>
                <div>halfspeed ... {track.halfspeed ? 'yes' : 'no'}</div>
                <div>chords ...... {track.instruments?.chordal || '—'}</div>
                <div>lead ........ {track.instruments?.lead || '—'}</div>
                <div>bass ........ {track.instruments?.bass || '—'}</div>
                {track.progression && Array.isArray(track.progression) && (
                  <div>
                    prog ........ {track.progression
                      .map((c) => Array.isArray(c) ? NOTE_NAMES[((c[0] % 12) + 12) % 12] : '?')
                      .join(' ')}
                  </div>
                )}
                {track.progTemplate != null && (
                  <div>template .... {track.mode || 'major'}-{track.progTemplate}</div>
                )}

                {/* Sub-sections at lower hierarchy level */}
                {track.introName && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 8, letterSpacing: 1.5 }}>— intro —</div>
                    <div>{track.introName.replace(/_/g, ' ')}</div>
                  </div>
                )}

                {(track.ambienceName || track.vinylName) && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 8, letterSpacing: 1.5 }}>— beds —</div>
                    {track.ambienceName && <div>amb ......... {track.ambienceName.replace(/_/g, ' ')}</div>}
                    {track.vinylName && <div>vinyl ....... {track.vinylName.replace(/_/g, ' ')}</div>}
                  </div>
                )}

                {track.artifacts && track.artifacts.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 8, letterSpacing: 1.5 }}>— artifacts —</div>
                    {track.artifacts.map((a, i) => (
                      <div key={i}>{a.name.replace(/_/g, ' ')} <span style={{ opacity: 0.5 }}>@ {a.fireAtSeconds}s</span></div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ opacity: 0.45, fontStyle: 'italic' }}>press play to tune in</div>
            )}

            {/* === KEYS (tertiary reference, bottom) === */}
            <div style={{
              marginTop: 14,
              paddingTop: 8,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.35)',
              fontSize: 8,
              letterSpacing: 1.5,
              lineHeight: '1.8',
            }}>
              <div>space — play/stop</div>
              <div>&gt; — next</div>
              <div>i — info</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
